import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

interface BulkVatAndDepartmentUpdateDialogProps {
  open: boolean;
  loading: boolean;
  selectedCount: number;
  vatRate: number;
  departmentNo: number;
  onVatRateChange: (value: number) => void;
  onDepartmentNoChange: (value: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

// Türkiye'deki yaygın KDV oranları
const VAT_RATE_OPTIONS = [0, 1, 8, 10, 18, 20];

export const BulkVatAndDepartmentUpdateDialog: React.FC<BulkVatAndDepartmentUpdateDialogProps> = ({
  open,
  loading,
  selectedCount,
  vatRate,
  departmentNo,
  onVatRateChange,
  onDepartmentNoChange,
  onConfirm,
  onCancel,
}) => {
  const isValidDepartmentNo = departmentNo >= 1 && departmentNo <= 100;

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TuneIcon color="secondary" />
        Toplu KDV + ÖKC Güncelleme
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          <strong>{selectedCount}</strong> ürünün hem KDV oranını hem de ÖKC (Departman Numarası) değerini aynı anda güncellemek üzeresiniz.
        </DialogContentText>
        
        {/* KDV Oranı Bölümü */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            KDV Oranı
          </Typography>
          
          <FormControl fullWidth size="small">
            <InputLabel>KDV Oranı</InputLabel>
            <Select
              value={vatRate}
              label="KDV Oranı"
              onChange={(e) => onVatRateChange(Number(e.target.value))}
              disabled={loading}
            >
              {VAT_RATE_OPTIONS.map((rate) => (
                <MenuItem key={rate} value={rate}>
                  %{rate}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            type="number"
            label="Manuel KDV Oranı (%)"
            value={vatRate}
            onChange={(e) => onVatRateChange(Number(e.target.value))}
            disabled={loading}
            inputProps={{ min: 0, max: 100, step: 1 }}
            size="small"
            sx={{ mt: 1 }}
            InputProps={{
              endAdornment: <Typography color="text.secondary">%</Typography>
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ÖKC Bölümü */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            ÖKC (Departman Numarası)
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            Yazarkasa entegrasyonu için departman numarası. Geçerli aralık: <strong>1-100</strong>
          </Alert>
          
          <TextField
            fullWidth
            type="number"
            label="Departman Numarası"
            value={departmentNo}
            onChange={(e) => onDepartmentNoChange(Number(e.target.value))}
            disabled={loading}
            inputProps={{ min: 1, max: 100, step: 1 }}
            size="small"
            error={departmentNo !== 0 && !isValidDepartmentNo}
            helperText={
              departmentNo !== 0 && !isValidDepartmentNo 
                ? 'Departman numarası 1-100 arasında olmalıdır' 
                : ''
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          İptal
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="secondary"
          disabled={loading || !isValidDepartmentNo}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Güncelleniyor...' : `${selectedCount} Ürünü Güncelle`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkVatAndDepartmentUpdateDialog;

